import React, { useState, useEffect, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import ToastAlert, { ToastCloseIcon } from "../ToastAlert/ToastAlert.jsx";

import LoadingScreen from "../SSORequestAccountForm/components/LoadingScreen.jsx";

import PageBanner from "../PageBanner/PageBanner.jsx";
import ContainerBleed from "../Container/ContainerBleed.jsx";
import ContainerFull from "../Container/ContainerFull.jsx";
import AccountRow from "./subcomponents/AccountRow.jsx";
import AccountPagination from "./subcomponents/AccountPagination.jsx";
import SortableHeader from "./subcomponents/SortableHeader.jsx";
import NotesModal from "./subcomponents/NotesModal.jsx";

/**
 * Pulls a human-readable message out of a failed Web API response so the
 * bulk-update results modal can show *why* a row failed instead of a bare
 * "Failed with status 400".
 *
 * ASP.NET Web API shapes error bodies as:
 *   - BadRequest(string)      -> { "message": "..." }
 *   - BadRequest(ModelState)  -> { "message": "The request is invalid.",
 *                                  "modelState": { "field": ["msg1", "msg2"] } }
 *   - unhandled exceptions    -> { "message": "...", "exceptionMessage": "..." }
 * It can also be a non-JSON HTML page (e.g. an auth redirect), which we fall
 * back to as raw text when it's short enough to be useful.
 */
const readServerError = async response => {
  let rawBody = "";
  try {
    rawBody = await response.text();
  } catch (e) {
    return `HTTP ${response.status}`;
  }

  let data = null;
  try {
    data = rawBody ? JSON.parse(rawBody) : null;
  } catch (e) {
    // Body wasn't JSON; we'll fall back to the raw text below.
  }

  if (data) {
    // Surface ModelState validation errors (one entry per invalid field).
    const modelState = data.modelState || data.ModelState;
    if (modelState) {
      const messages = Object.keys(modelState)
        .reduce((acc, key) => acc.concat(modelState[key]), [])
        .filter(Boolean);
      if (messages.length) {
        return messages.join(" ");
      }
    }
    // Prefer the friendly message; fall back to the exception detail.
    const message =
      data.message || data.Message || data.exceptionMessage || data.ExceptionMessage;
    if (message) {
      return message;
    }
  }

  // Non-JSON but short enough to be meaningful (avoid dumping a full HTML page).
  if (rawBody && rawBody.length < 300) {
    return rawBody;
  }
  return `HTTP ${response.status}`;
};

const SSOAccountSearch = props => {
  const { businessUnits } = props;
  const [loading, setLoading] = useState(false);
  const [accountStatus, setAccountStatus] = useState("active");
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAccountIds, setSelectedIds] = useState([]);
  // Snapshot map of full account objects keyed by userId. Needed so we can
  // filter selections against activeFilters even when the selected rows live
  // on another page (and are therefore not currently in accountData).
  const [selectedAccountsById, setSelectedAccountsById] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const headerCheckboxRef = useRef(null);
  const [showFilterPopout, setShowFilterPopout] = useState(false);
  const [filterField, setFilterField] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [activeFilters, setActiveFilters] = useState([
    { field: "status", value: "0" },
    { field: "source", value: "VU" },
  ]);

  // Bulk update modal state
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [bulkUpdateResults, setBulkUpdateResults] = useState([]);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  // Resend pending request emails state
  const [isResending, setIsResending] = useState(false);

  // Notes modal state
  const [notesAccount, setNotesAccount] = useState(null);

  // Track status changes for selected accounts
  const [accountStatusChanges, setAccountStatusChanges] = useState({});

  // Track all field changes for selected accounts
  const [accountFieldChanges, setAccountFieldChanges] = useState({});

  // API data state
  const [accountData, setUserData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);

  // Search/filter parameters for API
  const [searchParams, setSearchParams] = useState({
    status: 0, // Default to pending (0)
    source: "VU", // Default to VU source
    firstName: "",
    lastName: "",
    email: "",
    businessLine: "",
    companyName: "",
    pageNumber: 1,
    pageSize: 10,
    sortBy: "RequestedDate",
    sortOrder: "DESC",
  });

  // Use API pagination data instead of client-side calculations
  const totalAccounts = totalRecords;
  const paginatedAccounts = accountData; // API already returns paginated data

  const statusToInt = {
    pending: 0,
    active: 1,
    inactive: 3,
    rejected: 4,
  };

  const statusToString = {
    0: "pending",
    1: "active",
    3: "inactive",
    4: "rejected",
  };

  // Fetch users from API
  const fetchUsers = async (params = searchParams) => {
    setLoading(true);
    try {
      // Build query string from params
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (
          params[key] !== null &&
          params[key] !== undefined &&
          params[key] !== ""
        ) {
          queryParams.append(key, params[key]);
        }
      });

      const response = await fetch(
        `/sitecore/shell/B2C/AdminManagement/GetAllRequestUserListAsync?${queryParams.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Transform API response to match component's data structure
      const transformedUsers = data.users.map(user => ({
        ...user,
        accountStatus: statusToString[user.status] || "pending",
      }));

      setUserData(transformedUsers);
      setTotalRecords(data.totalRecords);
      setTotalPages(data.totalPages);
      setCurrentPage(data.currentPage);
      setHasPreviousPage(data.hasPreviousPage);
      setHasNextPage(data.hasNextPage);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast(
        <ToastAlert
          style="error"
          title="Failed to Load Users"
          description="Unable to retrieve user data. Please try again."
        />,
        {
          className: "st-toast-alert",
          hideProgressBar: true,
          position: "bottom-right",
          closeButton: <ToastCloseIcon />,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      // Build query string from current search params, excluding pagination/sorting
      const queryParams = new URLSearchParams();
      Object.keys(searchParams).forEach(key => {
        if (key === "pageNumber" || key === "pageSize") return;
        const value = searchParams[key];
        if (value !== null && value !== undefined && value !== "") {
          queryParams.append(key, value);
        }
      });

      const response = await fetch(
        `/sitecore/shell/B2C/AdminManagement/ExportUsersCsvAsync?${queryParams.toString()}`,
        {
          method: "GET",
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Try to extract filename from Content-Disposition header
      let filename = "AccountApproval.csv";
      const disposition = response.headers.get("Content-Disposition");
      if (disposition) {
        const match = disposition.match(
          /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i,
        );
        if (match && match[1]) {
          filename = decodeURIComponent(match[1]);
        }
      }

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting users CSV:", error);
      toast(
        <ToastAlert
          style="error"
          title="Failed to Export CSV"
          description="Unable to export user data. Please try again."
        />,
        {
          className: "st-toast-alert",
          hideProgressBar: true,
          position: "bottom-right",
          closeButton: <ToastCloseIcon />,
        },
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handlePageChange = newPage => {
    const newParams = {
      ...searchParams,
      pageNumber: newPage,
    };
    setSearchParams(newParams);
    fetchUsers(newParams);
  };

  const handlePerPageChange = newPerPage => {
    setPerPage(newPerPage);
    const newParams = {
      ...searchParams,
      pageNumber: 1,
      pageSize: newPerPage,
    };
    setSearchParams(newParams);
    fetchUsers(newParams);
  };

  const resendPendingEmails = async () => {
    const pendingSelected = filteredSelectedIds
      .map(id => selectedAccountsById[id])
      .filter(
        account => account && account.accountStatus === "pending",
      );

    if (pendingSelected.length === 0) {
      toast(
        <ToastAlert
          style="error"
          title="No Pending Requests Selected"
          description="Please select one or more pending requests to resend their review emails."
        />,
        {
          className: "st-toast-alert",
          hideProgressBar: true,
          position: "bottom-right",
          closeButton: <ToastCloseIcon />,
        },
      );
      return;
    }

    setIsResending(true);
    try {
      const response = await fetch(
        "/sitecore/shell/B2C/AdminManagement/Pending/ResendEmailsAsync",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            Emails: pendingSelected.map(a => a.email),
          }),
        },
      );

      const rawBody = await response.text();
      let data = null;
      try {
        data = rawBody ? JSON.parse(rawBody) : null;
      } catch (parseError) {
        // Non-JSON response (e.g. HTML error page from an auth/redirect)
      }

      if (!response.ok) {
        const serverMessage =
          (data && (data.Message || data.message)) ||
          (rawBody && rawBody.length < 300 ? rawBody : "") ||
          `HTTP ${response.status}`;
        throw new Error(serverMessage);
      }

      const results = (data && data.results) || [];
      const successCount = results.filter(r => r.success).length;
      const failureCount = results.length - successCount;

      toast(
        <ToastAlert
          style={failureCount === 0 ? "success" : "error"}
          title={
            failureCount === 0
              ? "Review Emails Resent"
              : "Resend Completed With Errors"
          }
          description={
            failureCount === 0
              ? `Resent review emails for ${successCount} pending request${successCount !== 1 ? "s" : ""}.`
              : `Sent ${successCount} of ${results.length}. ${failureCount} failed.`
          }
        />,
        {
          className: "st-toast-alert",
          hideProgressBar: true,
          position: "bottom-right",
          closeButton: <ToastCloseIcon />,
        },
      );
    } catch (error) {
      console.error("Resend error:", error);
      toast(
        <ToastAlert
          style="error"
          title="Resend Failed"
          description={
            error && error.message
              ? `Unable to resend review emails. ${error.message}`
              : "Unable to resend review emails. Please try again."
          }
        />,
        {
          className: "st-toast-alert",
          hideProgressBar: true,
          position: "bottom-right",
          closeButton: <ToastCloseIcon />,
        },
      );
    } finally {
      setIsResending(false);
    }
  };

  const applyFilter = () => {
    if (filterField && filterValue) {
      setActiveFilters(prev => [
        ...prev,
        { field: filterField, value: filterValue },
      ]);
      // Update search params with new filter
      const newParams = {
        ...searchParams,
        [filterField]: filterValue,
        pageNumber: 1, // Reset to first page when filtering
      };
      setSearchParams(newParams);
      fetchUsers(newParams);
      setFilterField("");
      setFilterValue("");
      setShowFilterPopout(false);
    }
  };

  const handleSort = (column, order) => {
    const newParams = {
      ...searchParams,
      sortBy: column,
      sortOrder: order,
      pageNumber: 1, // Reset to first page when sorting changes
    };
    setSearchParams(newParams);
    fetchUsers(newParams);
  };

  toast.onChange(payload => {
    console.log("Toast changed:", payload);
  });

  useEffect(() => {
    // this component exists on its own separate .aspx page
    // we need it to inherit VU styles, so we add the theme to body outselves
    document.body.classList.add("theme-vu");
    fetchUsers();
  }, []);

  const toggleAccountSelected = userId => {
    const account = accountData.find(a => a.userId === userId);
    setSelectedIds(prev => {
      const exists = prev.includes(userId);
      if (exists) {
        return prev.filter(id => id !== userId);
      }
      // If selecting a pending account, automatically set status to active
      if (account?.accountStatus === "pending") {
        setAccountStatusChanges(prevChanges => ({
          ...prevChanges,
          [userId]: "active",
        }));
      }
      return [...prev, userId];
    });
    setSelectedAccountsById(prev => {
      if (prev[userId]) {
        const next = { ...prev };
        delete next[userId];
        return next;
      }
      return account ? { ...prev, [userId]: account } : prev;
    });
  };

  // Determine whether an account satisfies every currently-applied filter.
  // Used to hide selections from the count/actions when they don't match the
  // active filter set (e.g. a pending selection while filtering by Active).
  const accountMatchesActiveFilters = account => {
    if (!account) return false;
    return activeFilters.every(f => {
      if (f.field === "status") {
        return statusToInt[account.accountStatus] === Number(f.value);
      }
      const val = account[f.field];
      if (val == null) return false;
      return String(val)
        .toLowerCase()
        .includes(String(f.value).toLowerCase());
    });
  };

  const filteredSelectedIds = selectedAccountIds.filter(id =>
    accountMatchesActiveFilters(selectedAccountsById[id]),
  );

  // Only count selections that are still visible in the current result set,
  // so filter/sort/page changes don't retain checkbox state for rows that are
  // no longer displayed.
  const visibleSelectedIds = selectedAccountIds.filter(id =>
    accountData.some(a => a.userId === id),
  );
  const allAccountsSelected =
    accountData &&
    accountData.length > 0 &&
    visibleSelectedIds.length === accountData.length;
  const someAccountsSelected =
    visibleSelectedIds.length > 0 && !allAccountsSelected;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someAccountsSelected;
    }
  }, [someAccountsSelected]);

  // When all filters are removed, clear any selected rows and pending edits so
  // stale selections from a previously filtered view don't linger.
  useEffect(() => {
    if (activeFilters.length === 0) {
      setSelectedIds([]);
      setSelectedAccountsById({});
      setAccountStatusChanges({});
      setAccountFieldChanges({});
      setIsEditMode(false);
    }
  }, [activeFilters]);

  const toggleAllSelected = () => {
    console.log("toggleAllSelected called");
    if (allAccountsSelected) {
      const currentPageIds = new Set(accountData.map(a => a.userId));
      setSelectedIds(prev => prev.filter(id => !currentPageIds.has(id)));
      setSelectedAccountsById(prev => {
        const next = { ...prev };
        currentPageIds.forEach(id => delete next[id]);
        return next;
      });
    } else {
      setSelectedIds(prev =>
        Array.from(new Set([...prev, ...accountData.map(a => a.userId)])),
      );
      setSelectedAccountsById(prev => {
        const next = { ...prev };
        accountData.forEach(a => {
          next[a.userId] = a;
        });
        return next;
      });
    }
  };

  const handleStatusChange = (userId, newStatus) => {
    setAccountStatusChanges(prev => ({
      ...prev,
      [userId]: newStatus,
    }));
  };

  const handleFieldChange = (userId, fieldName, value) => {
    setAccountFieldChanges(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [fieldName]: value,
      },
    }));
  };

  const applyBulkChanges = async () => {
    // A note is compulsory whenever an account's status is being changed.
    // Operate only on selections that satisfy the currently-applied filters,
    // consistent with what the user sees in the "N Accounts Selected" count.
    const selectedAccounts = filteredSelectedIds
      .map(id => selectedAccountsById[id])
      .filter(Boolean);
    const missingNote = selectedAccounts.find(account => {
      const originalStatus = account.accountStatus;
      const newStatus = accountStatusChanges[account.userId] || originalStatus;
      const note = (accountFieldChanges[account.userId] || {}).statusNote;
      return newStatus !== originalStatus && (!note || !note.trim());
    });

    if (missingNote !== undefined) {
      toast(
        <ToastAlert
          style="error"
          title="Note Required"
          description="A note is required when changing an account's status. Please add a note for each account whose status changed."
        />,
        {
          className: "st-toast-alert",
          hideProgressBar: true,
          position: "bottom-right",
          closeButton: <ToastCloseIcon />,
        },
      );
      return;
    }

    setShowBulkUpdateModal(true);
    setIsBulkUpdating(true);
    setBulkUpdateResults([]);

    for (const account of selectedAccounts) {
      const oldStatus = account.accountStatus;
      const newStatus =
        accountStatusChanges[account.userId] || account.accountStatus;
      const statusInt = statusToInt[newStatus];
      const statusChanged = oldStatus !== newStatus;

      // Get field changes for this account, or use original values
      const fieldChanges = accountFieldChanges[account.userId] || {};

      try {
        const response = await fetch(
          "/sitecore/shell/B2C/AdminManagement/UserRegistrationUpdateAsync",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              Email: account.email,
              Source: account.source,
              Status: statusInt,
              City: fieldChanges.city ?? account.city,
              CompanyName: fieldChanges.companyName ?? account.companyName,
              FirstName: fieldChanges.firstName ?? account.firstName,
              LastName: fieldChanges.lastName ?? account.lastName,
              JobTitle: fieldChanges.jobTitle ?? account.jobTitle,
              MobilePhone: fieldChanges.mobilePhone ?? account.mobilePhone,
              State: fieldChanges.state ?? account.state,
              ZipCode: fieldChanges.zipCode ?? account.zipCode,
              BusinessLine: fieldChanges.businessLine ?? account.businessLine,
              ModifiedBy: account.modifiedBy,
              NewsLetterSignUp: account.newsLetterSignUp,
              SMSMessage: account.smsMessage,
              OfficePhone: fieldChanges.officePhone ?? account.officePhone,
              DoingBusinessIn:
                fieldChanges.doingBusinessIn ?? account.doingBusinessIn,
              Note: fieldChanges.statusNote ?? "",
            }),
          },
        );

        if (response.ok) {
          setBulkUpdateResults(prev => [
            ...prev,
            {
              email: account.email,
              status: newStatus,
              statusChanged,
              success: true,
            },
          ]);
        } else {
          // Read the server's actual message (validation error, note required,
          // unexpected failure, etc.) so it shows per-row in the results modal.
          const serverError = await readServerError(response);
          setBulkUpdateResults(prev => [
            ...prev,
            {
              email: account.email,
              status: newStatus,
              statusChanged,
              success: false,
              error: serverError,
            },
          ]);
        }
      } catch (error) {
        setBulkUpdateResults(prev => [
          ...prev,
          {
            email: account.email,
            status: newStatus,
            statusChanged,
            success: false,
            error: error.message,
          },
        ]);
      }
    }

    setIsBulkUpdating(false);
  };

  const closeBulkUpdateModal = () => {
    setShowBulkUpdateModal(false);
    setBulkUpdateResults([]);
    setIsEditMode(false);
    setSelectedIds([]);
    setSelectedAccountsById({});
    setAccountStatusChanges({});
    setAccountFieldChanges({});
    // Refresh the data
    fetchUsers();
  };

  const updateAccountStatus = e => {
    e.preventDefault();
    // Function to update account status
    const formData = new FormData(e.target);
    const approvalStatus = formData.get("approval-status");

    console.log("Updating account status to:", e);

    setLoading(true);
    setShowModal(false);
    fetch("/sitecore/shell/B2C/AdminManagement/User/ManageAsync", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        Email: accountData.email,
        Source: accountData.source,
        Status: statusToInt[approvalStatus],
      }),
    })
      .then(response => {
        setLoading(false);
        if (response.ok) {
          setAccountStatus(approvalStatus);
          toast(
            <ToastAlert
              style="success"
              title="Success"
              description="Account status updated successfully."
            />,
            {
              className: "st-toast-alert",
              hideProgressBar: true,
              position: "bottom-right",
              closeButton: <ToastCloseIcon />,
            },
          );
          setShowModal(false);
        } else {
          throw new Error("Failed to update account status");
        }
      })
      .catch(error => {
        setLoading(false);
        console.error("Update error:", error);
        toast(
          <ToastAlert
            style="error"
            title="Update Failed"
            description="Unable to update account status. Please try again."
          />,
          {
            className: "st-toast-alert",
            hideProgressBar: true,
            position: "bottom-right",
            closeButton: <ToastCloseIcon />,
          },
        );
      });
  };

  return (
    <>
      <ContainerBleed backgroundColor="#f2f2f2">
        <PageBanner
          darkText={true}
          showAccentLine={true}
          title="Account Approval"
        />
      </ContainerBleed>
      <ContainerFull style={{ paddingBlock: "4rem" }}>
        <div className="sso-account-approval">
          {loading && <LoadingScreen />}
          <div className="sso-account-approval-table">
            <div className="sso-account-approval-table__header">
              <div className="sso-account-approval-table__header-status">
                Summary
              </div>
            </div>
            <div className="sso-account-approval__table-actions">
              <div>
                {!accountData ||
                  (accountData.length === 0 && (
                    <div>No user accounts found.</div>
                  ))}
                {filteredSelectedIds.length > 0 && (
                  <div className="sso-account-approval__selected-accounts">
                    {!isEditMode && (
                      <button
                        className="btn btn--primary"
                        onClick={() => setIsEditMode(true)}
                      >
                        Edit
                      </button>
                    )}
                    {filteredSelectedIds.length} Account
                    {filteredSelectedIds.length !== 1
                      ? "s"
                      : ""} Selected{" "}
                    {isEditMode && (
                      <>
                        <button
                          className="btn btn--primary"
                          onClick={applyBulkChanges}
                        >
                          Apply Changes
                        </button>
                        <button
                          className="btn"
                          onClick={() => setIsEditMode(false)}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                )}
                {isEditMode && filteredSelectedIds.length > 1 && (
                  <div>
                    <strong>Bulk Actions</strong> - Set all selected accounts to{" "}
                    <select
                      onChange={e => {
                        const newStatus = e.target.value;
                        const updates = {};
                        filteredSelectedIds.forEach(id => {
                          updates[id] = newStatus;
                        });
                        setAccountStatusChanges(prev => ({
                          ...prev,
                          ...updates,
                        }));
                      }}
                    >
                      <option value="">Select status...</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                )}
              </div>
              <div className="sso-account-approval__filters">
                {activeFilters.map((filter, index) => {
                  const fieldLabels = {
                    status: "Approval Status",
                    source: "Source",
                    firstName: "First Name",
                    lastName: "Last Name",
                    email: "Email",
                    mobilePhone: "Mobile Phone",
                    officePhone: "Office Phone",
                    city: "City",
                    state: "State",
                    zipCode: "ZIP Code",
                    companyName: "Company Name",
                    businessLine: "Business Line",
                    doingBusinessIn: "Doing Business In",
                    // These aren't supported yet
                    // stewartContactName: "Stewart Contact Name",
                    // stewartContactEmail: "Stewart Contact Email",
                  };

                  // Format the display value for status fields
                  const filterValueToString =
                    statusToString[filter.value] || filter.value;
                  const capitalizedDisplayValue =
                    filter.field === "status"
                      ? filterValueToString.charAt(0).toUpperCase() +
                        filterValueToString.slice(1)
                      : filter.value;

                  return (
                    <div
                      key={index}
                      className="sso-account-approval__filter-pill"
                    >
                      <span>
                        {fieldLabels[filter.field]}: {capitalizedDisplayValue}
                      </span>
                      <button
                        onClick={() => {
                          const filterToRemove = activeFilters[index];
                          setActiveFilters(prev =>
                            prev.filter((_, i) => i !== index),
                          );

                          // Update search params by removing this filter
                          const newParams = {
                            ...searchParams,
                            [filterToRemove.field]: "",
                            pageNumber: 1, // Reset to first page when removing filter
                          };
                          setSearchParams(newParams);
                          fetchUsers(newParams);
                        }}
                        className="sso-account-approval__filter-pill-remove"
                        aria-label="Remove filter"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
                <div className="sso-account-approval__filter-button-wrapper">
                  <button
                    className="btn btn--primary"
                    onClick={resendPendingEmails}
                    disabled={
                      isResending ||
                      filteredSelectedIds
                        .map(id => selectedAccountsById[id])
                        .filter(
                          a => a && a.accountStatus === "pending",
                        ).length === 0
                    }
                    title="Resend review emails to the Stewart Contact and Business Line for the selected pending requests"
                  >
                    {isResending ? "Resending..." : "Resend"}
                  </button>
                </div>
                <div className="sso-account-approval__filter-button-wrapper">
                  <button
                    className="btn btn--primary"
                    onClick={() => setShowFilterPopout(!showFilterPopout)}
                  >
                    + Add Filter
                  </button>
                  <button
                    className="btn btn--primary"
                    onClick={handleExportCsv}
                    disabled={isExporting}
                    title="Export the currently filtered users as a CSV file"
                    style={{ marginLeft: "0.5rem" }}
                  >
                    {isExporting ? "Exporting..." : "Export to CSV"}
                  </button>
                  {showFilterPopout && (
                    <div className="sso-account-approval__filter-popout">
                      <div className="sso-account-approval__filter-popout-field">
                        <label
                          htmlFor="filter-field"
                          className="sso-account-approval__filter-popout-label"
                        >
                          Field
                        </label>
                        <select
                          id="filter-field"
                          value={filterField}
                          onChange={e => setFilterField(e.target.value)}
                          className="sso-account-approval__filter-popout-select"
                        >
                          <option value="">Select a field...</option>
                          {!activeFilters.some(f => f.field === "source") && (
                            <option value="source">Source</option>
                          )}
                          {!activeFilters.some(f => f.field === "status") && (
                            <option value="status">Approval Status</option>
                          )}
                          {!activeFilters.some(
                            f => f.field === "firstName",
                          ) && <option value="firstName">First Name</option>}
                          {!activeFilters.some(f => f.field === "lastName") && (
                            <option value="lastName">Last Name</option>
                          )}
                          {!activeFilters.some(f => f.field === "email") && (
                            <option value="email">Email</option>
                          )}
                          {!activeFilters.some(
                            f => f.field === "companyName",
                          ) && (
                            <option value="companyName">Company Name</option>
                          )}
                          {!activeFilters.some(
                            f => f.field === "businessLine",
                          ) && (
                            <option value="businessLine">Business Line</option>
                          )}
                          {/*
                            
                            Not supported yet

                             {!activeFilters.some(
                              f => f.field === "stewartContactName",
                            ) && (
                              <option value="stewartContactName">
                                Stewart Contact Name
                              </option>
                            )}
                            {!activeFilters.some(
                              f => f.field === "stewartContactEmail",
                            ) && (
                              <option value="stewartContactEmail">
                                Stewart Contact Email
                              </option>
                            )} */}
                        </select>
                      </div>
                      <div className="sso-account-approval__filter-popout-field">
                        <label
                          htmlFor="filter-value"
                          className="sso-account-approval__filter-popout-label"
                        >
                          Value
                        </label>
                        {filterField === "status" ? (
                          <select
                            id="filter-value"
                            value={filterValue}
                            onChange={e => setFilterValue(e.target.value)}
                            className="sso-account-approval__filter-popout-select"
                          >
                            <option value="">Select status...</option>
                            <option value="0">Pending</option>
                            <option value="1">Active</option>
                            <option value="3">Inactive</option>
                            <option value="4">Rejected</option>
                          </select>
                        ) : filterField === "source" ? (
                          <select
                            id="filter-value"
                            value={filterValue}
                            onChange={e => setFilterValue(e.target.value)}
                            className="sso-account-approval__filter-popout-select"
                          >
                            <option value="">Select source...</option>
                            <option value="VU">VU</option>
                            <option value="StewartCom">StewartCom</option>
                          </select>
                        ) : filterField === "businessLine" ? (
                          <select
                            id="filter-value"
                            value={filterValue}
                            onChange={e => setFilterValue(e.target.value)}
                            className="sso-account-approval__filter-popout-select"
                          >
                            <option value="">Select business line...</option>
                            {businessUnits.map((unit, idx) => (
                              <option key={idx} value={unit}>
                                {unit}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            id="filter-value"
                            type="text"
                            value={filterValue}
                            onChange={e => setFilterValue(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                applyFilter();
                              }
                            }}
                            placeholder="Enter filter value..."
                            className="sso-account-approval__filter-popout-input"
                          />
                        )}
                      </div>
                      <div className="sso-account-approval__filter-popout-actions">
                        <button
                          className="btn"
                          onClick={() => {
                            setShowFilterPopout(false);
                            setFilterField("");
                            setFilterValue("");
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn--primary"
                          disabled={!filterField || !filterValue}
                          onClick={applyFilter}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="sso-account-approval-table__wrapper">
              {accountData && accountData.length > 0 && (
                <table className="sso-account-approval-table__table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          ref={headerCheckboxRef}
                          checked={allAccountsSelected}
                          onChange={toggleAllSelected}
                          disabled={isEditMode}
                        />
                      </th>
                      <SortableHeader
                        label="Source"
                        column="Source"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="Approval Status"
                        column="Status"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="Requested Date"
                        column="RequestedDate"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="First Name"
                        column="FirstName"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="Last Name"
                        column="LastName"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="Email"
                        column="Email"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="Mobile Phone"
                        column="MobilePhone"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="Office Phone"
                        column="OfficePhone"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="City"
                        column="City"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="State"
                        column="State"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="ZIP Code"
                        column="ZipCode"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="Company Name"
                        column="CompanyName"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="Business Line"
                        column="BusinessLine"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="Doing Business In"
                        column="DoingBusinessIn"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="Stewart Contact Name"
                        column="StewartContact"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="Stewart Contact Email"
                        column="StewartContactEmail"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="Approved By"
                        column="ApprovedBy"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="Approved Date"
                        column="ApprovedDate"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <SortableHeader
                        label="Last Login Date (Central Time)"
                        column="LastLoginDate"
                        searchParams={searchParams}
                        handleSort={handleSort}
                      />
                      <th>History &amp; Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedAccounts.map((account, index) => {
                      const fieldChanges =
                        accountFieldChanges[account.userId] || {};
                      return (
                        <AccountRow
                          key={account.userId || index}
                          account={account}
                          index={index}
                          selected={selectedAccountIds.includes(account.userId)}
                          onToggle={() => toggleAccountSelected(account.userId)}
                          isEditMode={isEditMode}
                          onStatusChange={handleStatusChange}
                          onFieldChange={handleFieldChange}
                          onOpenNotes={setNotesAccount}
                          currentStatus={
                            accountStatusChanges[account.userId] ||
                            account.accountStatus
                          }
                          fieldChanges={fieldChanges}
                          businessUnits={businessUnits}
                        />
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <AccountPagination
              currentPage={currentPage}
              totalPages={totalPages}
              perPage={perPage}
              totalAccounts={totalAccounts}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </div>
          {showBulkUpdateModal && (
            <div className="sso-account-approval__modal sso-account-approval__modal--bulk-update">
              <div className="sso-account-approval__modal-content">
                <div className="sso-account-approval__modal-title txt-h3">
                  {isBulkUpdating ? "Updating Accounts..." : "Update Complete"}
                </div>
                <div className="sso-account-approval__bulk-update-results">
                  {isBulkUpdating && bulkUpdateResults.length === 0 && (
                    <p>Starting updates...</p>
                  )}
                  {bulkUpdateResults.map((result, idx) => (
                    <div
                      key={idx}
                      className={`sso-account-approval__update-result ${
                        result.success ? "success" : "error"
                      }`}
                    >
                      {result.email} -{" "}
                      {result.success
                        ? result.statusChanged
                          ? `Updated to ${result.status}`
                          : "Updated successfully"
                        : `Failed: ${result.error}`}
                    </div>
                  ))}
                  {isBulkUpdating && (
                    <div className="sso-account-approval__update-result">
                      <em>Processing...</em>
                    </div>
                  )}
                </div>
                {!isBulkUpdating && (
                  <button
                    className="btn btn--primary"
                    onClick={closeBulkUpdateModal}
                    style={{ marginTop: "1rem" }}
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          )}
          {notesAccount && (
            <NotesModal
              account={notesAccount}
              onClose={() => setNotesAccount(null)}
            />
          )}
        </div>
      </ContainerFull>
      <ToastContainer autoClose={false} />
    </>
  );
};

export default SSOAccountSearch;
